import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ChangePasswordModal from './ChangePasswordModal';
import api from '../../../api/axios';
import { isValidGstin, normalizeGstin } from '../../../utils/gstin';

const AccountTab = () => {
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const [userDetails, setUserDetails] = useState({
    userName: '',
    email: '',
    role: ''
  });

  const [billingDetails, setBillingDetails] = useState({
    legalBusinessName: '',
    cin: '',
    country: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    gstNumber: ''
  });

  const [tempBillingDetails, setTempBillingDetails] = useState({ ...billingDetails });

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const response = await api.get('/api/v1/settings/account');
      const data = response.data?.data;
      if (data) {
        setUserDetails({
          userName: data.userName || '',
          email: data.email || '',
          role: data.role || ''
        });
        const billing = {
          legalBusinessName: data.legalBusinessName || '',
          cin: data.cin || '',
          country: data.country || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          pinCode: data.pinCode || '',
          gstNumber: data.gstNumber || ''
        };
        setBillingDetails(billing);
        setTempBillingDetails(billing);
      }
    } catch (error) {
      console.error('Failed to fetch account:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setTempBillingDetails({ ...billingDetails });
  };

  const [saving, setSaving] = useState(false);

  const gstInvalid = (tempBillingDetails.gstNumber || '').trim().length > 0
    && !isValidGstin(tempBillingDetails.gstNumber);

  const handleSaveClick = async () => {
    if (gstInvalid) return;
    setSaving(true);
    try {
      await api.put('/api/v1/settings/account', {
        country: tempBillingDetails.country,
        address: tempBillingDetails.address,
        city: tempBillingDetails.city,
        state: tempBillingDetails.state,
        pinCode: tempBillingDetails.pinCode,
        gstNumber: (tempBillingDetails.gstNumber || '').trim()
          ? normalizeGstin(tempBillingDetails.gstNumber)
          : ''
      });
      setBillingDetails({ ...tempBillingDetails });
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to update account:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelClick = () => {
    setTempBillingDetails({ ...billingDetails });
    setIsEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setTempBillingDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[0, 1].map((i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-muted" />
                <div className="h-5 w-32 bg-muted rounded" />
              </div>
              <div className="space-y-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j}><div className="h-4 w-20 bg-muted rounded mb-1.5" /><div className="h-10 w-full bg-muted rounded" /></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT SECTION - USER DETAILS */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">User Details</h3>
              <p className="text-xs text-muted-foreground">Your personal information</p>
            </div>
          </div>

          <div className="space-y-4">
            <Input
              label="User Name"
              type="text"
              value={userDetails.userName}
              placeholder="Enter your name"
              disabled
              className="transition-all focus:scale-[1.01]"
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                User Access Level
              </label>
              <div className="flex items-center gap-2 p-3 bg-muted/50 border border-border rounded-md">
                <Icon name="Shield" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{userDetails.role || 'N/A'}</span>
                <span className="ml-auto px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Non-editable</span>
              </div>
            </div>

            <Input
              label="Email"
              type="email"
              value={userDetails.email}
              placeholder="Enter your email"
              disabled
              className="transition-all focus:scale-[1.01]"
            />

            <div className="pt-2">
              <Button
                variant="outline"
                iconName="Lock"
                iconPosition="left"
                onClick={() => setIsChangePasswordModalOpen(true)}
                className="w-full hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all hover:scale-[1.02]"
              >
                Change Password
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION - COMPANY & BILLING DETAILS */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Building" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Company & Billing Details</h3>
              <p className="text-xs text-muted-foreground">Business information</p>
            </div>
          </div>

          <div className="space-y-4">
            <Input
              label="Legal Business Name"
              type="text"
              value={billingDetails?.legalBusinessName}
              disabled
              className="transition-all focus:scale-[1.01]"
            />

            <Input
              label="CIN Number"
              type="text"
              value={billingDetails?.cin}
              disabled
              className="transition-all focus:scale-[1.01]"
            />

            <Input
              label="Country"
              type="text"
              value={isEditMode ? tempBillingDetails?.country : billingDetails?.country}
              onChange={(e) => handleInputChange('country', e?.target?.value)}
              disabled={!isEditMode}
              className="transition-all focus:scale-[1.01]"
            />

            <Input
              label="Address"
              type="text"
              value={isEditMode ? tempBillingDetails?.address : billingDetails?.address}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              disabled={!isEditMode}
              className="transition-all focus:scale-[1.01]"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="City"
                type="text"
                value={isEditMode ? tempBillingDetails?.city : billingDetails?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                disabled={!isEditMode}
                className="transition-all focus:scale-[1.01]"
              />
              <Input
                label="State"
                type="text"
                value={isEditMode ? tempBillingDetails?.state : billingDetails?.state}
                onChange={(e) => handleInputChange('state', e?.target?.value)}
                disabled={!isEditMode}
                className="transition-all focus:scale-[1.01]"
              />
            </div>

            <Input
              label="PIN Code"
              type="text"
              value={isEditMode ? tempBillingDetails?.pinCode : billingDetails?.pinCode}
              onChange={(e) => handleInputChange('pinCode', e?.target?.value)}
              disabled={!isEditMode}
              className="transition-all focus:scale-[1.01]"
            />

            <div>
              <Input
                label="GST Number"
                type="text"
                value={isEditMode ? tempBillingDetails?.gstNumber : billingDetails?.gstNumber}
                onChange={(e) => handleInputChange('gstNumber', (e?.target?.value || '').toUpperCase())}
                disabled={!isEditMode}
                maxLength={15}
                placeholder={isEditMode ? '22AAAAA0000A1Z5' : ''}
                className="transition-all focus:scale-[1.01]"
              />
              {isEditMode && gstInvalid && (
                <p className="text-[11px] text-red-600 mt-1">Enter a valid 15-character GSTIN or leave blank.</p>
              )}
            </div>

            <div className="pt-2">
              {!isEditMode ? (
                <Button
                  variant="default"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={handleEditClick}
                  className="w-full hover:scale-[1.02] transition-all"
                >
                  Edit Billing Details
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant="default"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSaveClick}
                    disabled={saving || gstInvalid}
                    className="flex-1 hover:scale-[1.02] transition-all"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    iconName="X"
                    iconPosition="left"
                    onClick={handleCancelClick}
                    className="flex-1 hover:scale-[1.02] transition-all"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default AccountTab;
